import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { MdGavel } from "react-icons/md";
import ExpandAllButton from "../ExpandAllButton";
import { EmptyState, LoadingCards } from "../ListStates";
import SearchToolbar from "../SearchToolbar";
import SectionHeader from "../SectionHeader";
import ViewSwitch from "../ViewSwitch";
import CarrierCard from "./CarrierCard";
import CarrierListView from "./CarrierListView";
import CarrierRow from "./CarrierRow";
import ComingSoonSection from "../ComingSoon/ComingSoonSection";
// isOn and serviceEntries return with allServicesOn once the bulk services API exists.
import { carrierName } from "../../carrierHelpers";

// Disabled until the bulk services API exists: one request per service was too slow.
// const allServicesOn = (row) =>
//   Object.fromEntries(
//     serviceEntries(row)
//       .filter(([, value]) => !isOn(value))
//       .map(([key]) => [key, true]),
//   );

const NetworkTab = ({ rows, loading, busy, setEnabled, setServices, upcoming }) => {
  const { t } = useTranslation("settings");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("cards");
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});

  const query = search.trim().toLowerCase();
  const visible = rows.filter((row) => carrierName(row).toLowerCase().includes(query));
  const allExpanded = visible.length > 0 && visible.every((row) => expanded[row.id]);
  const selectedRows = visible.filter((row) => selected[row.id]);
  const upcomingVisible = upcoming
    .map((group) => ({
      ...group,
      items: group.items.filter((carrier) => carrier.name.toLowerCase().includes(query)),
    }))
    .filter((group) => group.items.length);

  const toggleExpanded = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleSelected = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleAllExpanded = () =>
    setExpanded(allExpanded ? {} : Object.fromEntries(visible.map((row) => [row.id, true])));
  const toggleAllSelected = () =>
    setSelected(
      selectedRows.length === visible.length
        ? {}
        : Object.fromEntries(visible.map((row) => [row.id, true])),
    );

  const changeServices = (row, changes) => setServices([row], () => changes);

  const renderCarriers = () => {
    if (loading) return <LoadingCards />;
    if (!visible.length) {
      return (
        <EmptyState>
          {query ? t("carriers.noLiveMatch") : t("carriers.noneAvailable")}
        </EmptyState>
      );
    }
    const cardProps = (row) => ({
      row,
      expanded: !!expanded[row.id],
      onExpand: () => toggleExpanded(row.id),
      busy: !!busy[row.id],
      onToggle: (next) => setEnabled([row], next),
      onServicesChange: (changes) => changeServices(row, changes),
    });
    return (
      <>
        <div className="divide-y divide-ui-border-subtle overflow-hidden rounded-xl border border-ui-border bg-ui-surface sm:hidden">
          {visible.map((row) => (
            <CarrierRow key={row.id} {...cardProps(row)} />
          ))}
        </div>
        {view === "list" ? (
          <div className="hidden sm:block">
            <CarrierListView
              rows={visible}
              expanded={expanded}
              onExpand={toggleExpanded}
              selected={selected}
              onSelect={toggleSelected}
              onSelectAll={toggleAllSelected}
              onClearSelection={() => setSelected({})}
              busy={busy}
              onToggle={(row, next) => setEnabled([row], next)}
              onServicesChange={changeServices}
              onBulkEnable={(next) => setEnabled(selectedRows, next)}
              // Disabled until the bulk services API exists.
              // onBulkServicesOn={() =>
              //   setServices(selectedRows, allServicesOn, "All services turned on")
              // }
            />
          </div>
        ) : (
          <div className="hidden grid-cols-[repeat(auto-fill,minmax(270px,1fr))] items-start gap-3 sm:grid">
            {visible.map((row) => (
              <CarrierCard key={row.id} {...cardProps(row)} />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <SearchToolbar value={search} onChange={setSearch} placeholder={t("carriers.searchCarriers")} />

      <section className="flex flex-col gap-3">
        <SectionHeader
          title={t("carriers.liveNow")}
          count={loading ? "–" : visible.length}
          hint={t("carriers.liveHint")}
        >
          <ExpandAllButton
            expanded={allExpanded}
            onClick={toggleAllExpanded}
            disabled={!visible.length}
          />
          <ViewSwitch value={view} onChange={setView} ariaLabel={t("carriers.carrierView")} />
        </SectionHeader>
        {renderCarriers()}
      </section>

      {!loading && <ComingSoonSection groups={upcomingVisible} searching={!!query} />}

      <div className="flex gap-2.5 rounded-[10px] border border-ui-border bg-ui-surface px-4 py-3.5">
        <MdGavel className="mt-px h-4 w-4 shrink-0 text-ui-text-muted" />
        <div className="flex flex-col gap-1.5 text-[11px] leading-relaxed text-state-neutral-text">
          <strong className="text-xs font-semibold text-ui-text">
            {t("carriers.notice.title")}
          </strong>
          <span>{t("carriers.notice.trademarks")}</span>
          <span>
            <Trans
              t={t}
              i18nKey="carriers.notice.availability"
              components={{
                mail: (
                  <a href="mailto:legal@hajexbolt.com" className="text-brand hover:underline">
                    legal@hajexbolt.com
                  </a>
                ),
              }}
            />
          </span>
        </div>
      </div>
    </>
  );
};

export default NetworkTab;
